(() => {
  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  const rafThrottle = (fn) => {
    let raf = 0;
    return (...args) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        fn(...args);
      });
    };
  };

  const parseBool = (v, def = false) => {
    if (v == null) return def;
    const s = String(v).toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
    return def;
  };

  const parseNumber = (v, def) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : def;
  };

  function initEmbla(root) {
    if (!root || !window.EmblaCarousel) return;

    // 防重复绑定（Shopify section reload / theme editor）
    if (root.__emblaBound) {
      try { root.__emblaDestroy?.(); } catch (_) { }
      try { root.__emblaApi?.destroy?.(); } catch (_) { }
    }
    root.__emblaBound = true;

    const viewportNode = root.querySelector(".embla__viewport");
    if (!viewportNode) return;

    const prevBtn = root.querySelector(".embla__button--prev");
    const nextBtn = root.querySelector(".embla__button--next");
    const scrollbar = root.querySelector(".embla__scrollbar");
    const thumb = root.querySelector(".embla__scrollbar-thumb");

    const DEBUG = parseBool(root.dataset.emblaDebug, false);
    const DBG_PREFIX = "[EmblaDBG]";
    const dbg = (...args) => { if (DEBUG) console.log(DBG_PREFIX, ...args); };
    const dbgGroup = (title, fn) => {
      if (!DEBUG) return fn?.();
      console.groupCollapsed(`${DBG_PREFIX} ${title}`);
      try { return fn?.(); } finally { console.groupEnd(); }
    };

    /* ---------- Dataset options ---------- */
    const centerMode = parseBool(root.dataset.centerMode, false);

    const loopMode = (root.dataset.loop || "auto").toLowerCase(); // true/false/auto
    const forceLoop = parseBool(root.dataset.forceLoop, false);

    const dragFree = parseBool(root.dataset.dragFree, false);
    const skipSnaps = parseBool(root.dataset.skipSnaps, false);
    const align = (root.dataset.align || (centerMode ? "center" : "start")).toLowerCase();

    const enableScrollbar = parseBool(root.dataset.scrollbar, true) && !!scrollbar && !!thumb;

    // wrap slide behavior (你想要的“从右滑到左”)
    const wrapSlide = parseBool(root.dataset.wrapSlide, true);
    const wrapSlideDuration = parseNumber(root.dataset.wrapSlideDuration, 280); // ms

    // Tween Scale
    const enableTweenScale = parseBool(root.dataset.tweenScale, false);
    const SCALE_MIN = parseNumber(root.dataset.scaleMin, 0.6);
    const SCALE_MAX = parseNumber(root.dataset.scaleMax, 1.0);
    const TWEEN_FACTOR_BASE = parseNumber(root.dataset.scaleFactor, 0.52);
    const isDesktop = () => window.matchMedia("(min-width: 700px)").matches;
    const tweenScaleMobile = parseBool(root.dataset.tweenScaleMobile, false);

    const shouldTween = () =>
      enableTweenScale &&
      centerMode &&
      (tweenScaleMobile ? true : isDesktop());

    // Benefits autoscroll
    const autoscrollEnabled =
      parseBool(root.dataset.autoscroll, false) ||
      (typeof root.dataset.embla === "string" && root.dataset.embla.startsWith("benefits"));

    const autoScrollSpeed = parseNumber(root.dataset.autoscrollSpeed, 0.4);
    const autoScrollResumeDelay = parseNumber(root.dataset.autoscrollResumeDelay, 1200);
    const autoScrollStopOnMouseEnter = parseBool(root.dataset.autoscrollStopOnMouseEnter, true);

    dbgGroup("config", () => {
      dbg("id:", root.id || "(no id)");
      dbg("centerMode:", centerMode, "align:", align);
      dbg("loopMode:", loopMode, "forceLoop:", forceLoop);
      dbg("dragFree:", dragFree, "skipSnaps:", skipSnaps);
      dbg("scrollbar:", enableScrollbar);
      dbg("wrapSlide:", wrapSlide, "wrapSlideDuration:", wrapSlideDuration);
      dbg("tweenScale:", enableTweenScale, "scaleMin/Max/factor:", SCALE_MIN, SCALE_MAX, TWEEN_FACTOR_BASE);
      dbg("autoscrollEnabled:", autoscrollEnabled, "hasAutoScrollPlugin:", !!window.EmblaCarouselAutoScroll);
    });

    /* ---------- Layout helpers ---------- */
    const OVERFLOW_EPS = 2;
    const DPR = Math.max(1, window.devicePixelRatio || 1);
    const devicePx = 1 / DPR;

    const getGapPx = (container) => {
      if (!container) return 0;
      const cs = getComputedStyle(container);
      return (parseFloat(cs.columnGap || cs.gap || "0") || 0);
    };

    const getContentWidth = (api) => {
      const container = api.containerNode ? api.containerNode() : viewportNode.querySelector(".embla__container");
      const slides = api.slideNodes ? api.slideNodes() : Array.from(viewportNode.querySelectorAll(".embla__slide"));
      if (!slides.length) return 0;

      console.log("container======",container);
      
      const gap = getGapPx(container);
      let sum = 0;

      for (const s of slides) {
        const r = s.getBoundingClientRect();
        const cs = getComputedStyle(s);
        const ml = parseFloat(cs.marginLeft || "0") || 0;
        const mr = parseFloat(cs.marginRight || "0") || 0;
        sum += r.width + ml + mr;
      }
      if (slides.length > 1) sum += gap * (slides.length - 1);
      return sum;
    };

    const isOverflowing = (api) => {
      const viewportW = viewportNode.getBoundingClientRect().width || viewportNode.clientWidth || 0;
      const contentW = getContentWidth(api);
      return contentW > viewportW + OVERFLOW_EPS;
    };

    /* ---------- Plugins ---------- */
    const plugins = [];
    if (autoscrollEnabled && window.EmblaCarouselAutoScroll) {
      plugins.push(
        EmblaCarouselAutoScroll({
          speed: autoScrollSpeed,
          stopOnInteraction: false,
          stopOnMouseEnter: false,
          playOnInit: true,
          stopOnLastSnap: false
        })
      );
    }

    /* ---------- Create Embla ---------- */
    let emblaApi = EmblaCarousel(
      viewportNode,
      {
        loop: false, // sync later
        dragFree,
        skipSnaps,
        align,
        containScroll: centerMode ? false : "trimSnaps"
      },
      plugins
    );
    root.__emblaApi = emblaApi;

    // ✅用 internalEngine().options 作为真相来源（稳定）
    const getEngine = () => {
      try { return emblaApi.internalEngine?.(); } catch (_) { return null; }
    };
    const getOpt = () => {
      const eng = getEngine();
      if (eng?.options) return eng.options;
      try {
        if (typeof emblaApi.options === "function") return emblaApi.options();
        if (emblaApi.options) return emblaApi.options;
      } catch (_) { }
      return {};
    };
    const isLooping = () => !!getOpt().loop;

    const getSlideCount = () => {
      try {
        return (emblaApi.slideNodes?.().length || viewportNode.querySelectorAll(".embla__slide").length || 1);
      } catch (_) {
        return viewportNode.querySelectorAll(".embla__slide").length || 1;
      }
    };

    const getSnapCount = () => {
      try { return emblaApi.scrollSnapList?.().length || 1; } catch (_) { return 1; }
    };

    /* =========================================================
     * Prev / Next
     * ========================================================= */
    const updatePrevNextState = () => {
      if (!prevBtn || !nextBtn || !emblaApi) return;

      const overflowing = isOverflowing(emblaApi);
      if (!overflowing) {
        prevBtn.setAttribute("disabled", "disabled");
        nextBtn.setAttribute("disabled", "disabled");
        return;
      }

      if (isLooping()) {
        prevBtn.removeAttribute("disabled");
        nextBtn.removeAttribute("disabled");
        return;
      }

      emblaApi.canScrollPrev()
        ? prevBtn.removeAttribute("disabled")
        : prevBtn.setAttribute("disabled", "disabled");

      emblaApi.canScrollNext()
        ? nextBtn.removeAttribute("disabled")
        : nextBtn.setAttribute("disabled", "disabled");
    };

    prevBtn?.addEventListener("click", () => {
      if (!emblaApi) return;
      if (!isOverflowing(emblaApi)) return;
      emblaApi.scrollPrev();
    }, { passive: true });

    nextBtn?.addEventListener("click", () => {
      if (!emblaApi) return;
      if (!isOverflowing(emblaApi)) return;
      emblaApi.scrollNext();
    }, { passive: true });

    /* =========================================================
     * Scrollbar
     * ========================================================= */
    let isThumbDragging = false;
    let isCarouselDragging = false;

    // loop：连续 slide progress
    let lastSlideProgRaw = null;
    let virtualSlideProg = 0;

    // non-loop：连续 scroll progress
    let virtualProg = 0;

    // loop wrap detect
    let lastSlideIndex = null;

    // smooth thumb
    let thumbOffset = 0;
    let targetOffset = 0;
    let smoothRaf = 0;

    // wrap sliding animation gate (关键：防止 Embla scrollProgress 覆盖)
    let wrapAnimActive = false;
    let wrapAnimRaf = 0;

    const cancelSmooth = () => {
      try { cancelAnimationFrame(smoothRaf); } catch (_) { }
      smoothRaf = 0;
    };
    const cancelWrapAnim = () => {
      try { cancelAnimationFrame(wrapAnimRaf); } catch (_) { }
      wrapAnimRaf = 0;
      wrapAnimActive = false;
    };

    const applyThumb = (offset, immediate = false) => {
      thumbOffset = offset;
      if (thumb) {
        thumb.style.transform = `translate3d(${offset}px,0,0)`;
        thumb.dataset.offset = String(offset);
      }
      if (immediate) targetOffset = offset;
    };

    const startSmooth = () => {
      if (smoothRaf || !thumb) return;
      const step = () => {
        smoothRaf = 0;
        if (!thumb || isThumbDragging || wrapAnimActive) return;

        const next = targetOffset;
        applyThumb(next, true);
        // Remove smoothing loop - we want direct sync
        // smoothRaf = requestAnimationFrame(step);
      };
      smoothRaf = requestAnimationFrame(step);
    };

    const getSlideIndexFromSnap = (snapIndex) => {
      try {
        const eng = getEngine();
        const reg = eng?.slideRegistry;
        const arr = reg?.[snapIndex];
        if (arr && arr.length) return arr[0];
      } catch (_) { }
      const n = Math.max(1, getSlideCount());
      return clamp(snapIndex, 0, n - 1);
    };

    const getSelectedSlideIndex = () => {
      const snap = emblaApi.selectedScrollSnap?.() ?? 0;
      const n = Math.max(1, getSlideCount());
      return clamp(getSlideIndexFromSnap(snap), 0, n - 1);
    };

    const getIndexProgressLoop = () => {
      const n = Math.max(1, getSlideCount());
      if (n <= 1) return 0;
      return getSelectedSlideIndex() / (n - 1);
    };

    const getIndexProgressNonLoop = () => {
      const n = Math.max(1, getSnapCount());
      if (n <= 1) return 0;
      const sel = emblaApi.selectedScrollSnap?.() ?? 0;
      return clamp(sel / (n - 1), 0, 1);
    };

    const getSlideProgressFromScrollProgress = () => {
      const nSlides = Math.max(1, getSlideCount());
      if (nSlides <= 1) return 0;

      const snaps = emblaApi.scrollSnapList?.() || [];
      const p = clamp(emblaApi.scrollProgress?.() ?? 0, 0, 1);
      if (snaps.length < 2) return getIndexProgressLoop();

      let i = 0;
      while (i < snaps.length - 1 && snaps[i + 1] < p) i++;

      const a = snaps[i];
      const b = snaps[Math.min(i + 1, snaps.length - 1)];
      const denom = Math.max(1e-6, (b - a));
      const t = clamp((p - a) / denom, 0, 1);

      const slideA = getSlideIndexFromSnap(i);
      const slideB = getSlideIndexFromSnap(Math.min(i + 1, snaps.length - 1));
      const slidePos = slideA + (slideB - slideA) * t;

      return clamp(slidePos / (nSlides - 1), 0, 1);
    };

    const updateVirtualProgress = () => {
      if (wrapAnimActive) return; // ✅wrap 动画期间，不要被 Embla 反复覆盖

      if (isLooping()) {
        const raw = getSlideProgressFromScrollProgress();
        if (lastSlideProgRaw == null) {
          lastSlideProgRaw = raw;
          virtualSlideProg = raw;
          return;
        }
        let delta = raw - lastSlideProgRaw;
        if (delta < -0.5) delta += 1;
        if (delta > 0.5) delta -= 1;
        virtualSlideProg += delta;
        lastSlideProgRaw = raw;
      } else {
        virtualProg = clamp(emblaApi.scrollProgress?.() ?? 0, 0, 1);
      }
    };

    const getMetrics = () => {
      const trackWidth = scrollbar ? scrollbar.getBoundingClientRect().width : 0;

      if (!isOverflowing(emblaApi)) {
        return { trackWidth, stepCount: 1, thumbWidth: trackWidth, maxOffset: 0 };
      }

      const stepCount = isLooping() ? Math.max(1, getSlideCount()) : Math.max(1, getSnapCount());
      const minThumbSize = 40;

      let thumbWidth = Math.max(minThumbSize, trackWidth / stepCount);
      thumbWidth = Math.ceil(thumbWidth * DPR) / DPR + devicePx; // 消灭尾部留白
      thumbWidth = Math.min(trackWidth, thumbWidth);

      const maxOffset = Math.max(0, trackWidth - thumbWidth);
      return { trackWidth, stepCount, thumbWidth, maxOffset };
    };

    const setThumbByProgress = (progress, maxOffset, immediate = false) => {
      const p = clamp(progress, 0, 1);
      targetOffset = maxOffset * p;

      if (immediate || isCarouselDragging) applyThumb(targetOffset, true);
      else startSmooth();
    };

    const startWrapSlide = (dir) => {
      if (!wrapSlide || !enableScrollbar || !scrollbar || !thumb) return;
      const { maxOffset } = getMetrics();

      // dir: 1 => last -> 0  (max -> 0),  -1 => 0 -> last (0 -> max)
      const from = dir === 1 ? maxOffset : 0;
      const to = dir === 1 ? 0 : maxOffset;

      cancelWrapAnim();
      cancelSmooth();
      wrapAnimActive = true;

      // 保证起点正确（防止刚好不在端点）
      applyThumb(from, true);

      const linear = (t) => t;
      const start = performance.now();
      const dur = Math.max(120, wrapSlideDuration);

      const tick = (now) => {
        if (!wrapAnimActive) return;
        const t = clamp((now - start) / dur, 0, 1);
        const eased = linear(t);
        const pos = from + (to - from) * eased;

        applyThumb(pos, true);

        if (t < 1) {
          wrapAnimRaf = requestAnimationFrame(tick);
        } else {
          wrapAnimActive = false;
          wrapAnimRaf = 0;

          // 动画结束后，把内部进度锚点对齐到端点，避免下一次 scroll 又把它拉回去
          if (dir === 1) {
            virtualSlideProg = 0;
            lastSlideProgRaw = 0;
            targetOffset = 0;
            applyThumb(0, true);
          } else {
            virtualSlideProg = 1;
            lastSlideProgRaw = 1;
            targetOffset = maxOffset;
            applyThumb(maxOffset, true);
          }
        }
      };

      wrapAnimRaf = requestAnimationFrame(tick);
    };

    const logState = (tag) => {
      if (!DEBUG) return;
      const opt = getOpt();
      const { trackWidth, stepCount, thumbWidth, maxOffset } = getMetrics();
      dbgGroup(tag, () => {
        dbg("loop:", !!opt.loop, "align:", opt.align, "containScroll:", opt.containScroll);
        dbg("overflow:", isOverflowing(emblaApi), "viewportW:", viewportNode.getBoundingClientRect().width);
        dbg("slideCount:", getSlideCount(), "snapCount:", getSnapCount(), "stepCount(used):", stepCount);
        dbg("selectedSnap:", emblaApi.selectedScrollSnap?.(), "selectedSlide:", getSelectedSlideIndex());
        dbg("scrollProgress:", emblaApi.scrollProgress?.(), "virtualSlideProg:", virtualSlideProg, "virtualProg:", virtualProg);
        dbg("indexProgress(loop):", getIndexProgressLoop(), "indexProgress(non-loop):", getIndexProgressNonLoop());
        dbg("trackWidth:", trackWidth, "thumbWidth:", thumbWidth, "maxOffset:", maxOffset);
        dbg("thumbOffset:", thumbOffset, "targetOffset:", targetOffset, "wrapAnimActive:", wrapAnimActive);
      });
    };

    const updateScrollbar = rafThrottle((mode = "auto") => {
      if (!enableScrollbar || !scrollbar || !thumb || !emblaApi) return;
      if (isThumbDragging) return;
      if (wrapAnimActive) return;

      const overflowing = isOverflowing(emblaApi);
      scrollbar.style.display = overflowing ? "" : "none";

      if (!overflowing) {
        cancelSmooth();
        applyThumb(0, true);
        return;
      }

      const { thumbWidth, maxOffset } = getMetrics();
      thumb.style.width = `${thumbWidth}px`;

      if (!isLooping()) {
        const p = (mode === "settle") ? getIndexProgressNonLoop() : clamp(virtualProg, 0, 1);
        setThumbByProgress(p, maxOffset, mode === "settle");
        return;
      }

      if (mode === "settle") {
        cancelSmooth();
        setThumbByProgress(getIndexProgressLoop(), maxOffset, true);
      } else {
        setThumbByProgress(virtualSlideProg, maxOffset);
      }
    });

    const scrollToProgress = (progress) => {
      const snaps = emblaApi.scrollSnapList?.() || [];
      const lastIndex = snaps.length - 1;
      if (lastIndex < 0) return;
      const targetIndex = Math.round(clamp(progress, 0, 1) * lastIndex);
      emblaApi.scrollTo(targetIndex);
    };

    // Scrollbar interactions
    if (enableScrollbar && scrollbar && thumb) {
      scrollbar.style.overflow = "hidden";
      thumb.style.willChange = "transform";
      thumb.style.transform = "translate3d(0px,0,0)";
      thumb.dataset.offset = "0";

      scrollbar.addEventListener("click", (event) => {
        if (event.target === thumb) return;
        if (!isOverflowing(emblaApi)) return;

        const rect = scrollbar.getBoundingClientRect();
        const { thumbWidth, maxOffset } = getMetrics();
        let offset = (event.clientX - rect.left) - thumbWidth / 2;
        offset = clamp(offset, 0, maxOffset);

        scrollToProgress(maxOffset === 0 ? 0 : offset / maxOffset);
      });

      let startX = 0;
      let startOffset = 0;

      const onMove = (event) => {
        const { maxOffset } = getMetrics();
        const offset = clamp(startOffset + (event.clientX - startX), 0, maxOffset);
        cancelWrapAnim();
        cancelSmooth();
        applyThumb(offset, true);
        scrollToProgress(maxOffset === 0 ? 0 : offset / maxOffset);
      };

      const onUp = (event) => {
        isThumbDragging = false;
        try { thumb.releasePointerCapture(event.pointerId); } catch (_) { }
        thumb.removeEventListener("pointermove", onMove);
        thumb.removeEventListener("pointerup", onUp);
        thumb.removeEventListener("pointercancel", onUp);
        updateScrollbar("settle");
        logState("thumbUp");
      };

      thumb.addEventListener("pointerdown", (event) => {
        if (!isOverflowing(emblaApi)) return;
        event.preventDefault();
        isThumbDragging = true;
        startX = event.clientX;
        startOffset = parseFloat(thumb.dataset.offset || "0") || 0;
        cancelWrapAnim();
        cancelSmooth();
        try { thumb.setPointerCapture(event.pointerId); } catch (_) { }
        thumb.addEventListener("pointermove", onMove);
        thumb.addEventListener("pointerup", onUp);
        thumb.addEventListener("pointercancel", onUp);
        logState("thumbDown");
      });
    }

    // pointer events
    emblaApi.on("pointerDown", () => { isCarouselDragging = true; });
    emblaApi.on("pointerUp", () => { isCarouselDragging = false; });

    /* ---------- Loop strategy sync ---------- */
    let lastLoop = null;
    let lastContainScroll = null;
    let lastAlign = null;

    const computeLoopShould = () => {
      if (loopMode === "true") return true;
      if (loopMode === "false") return false;
      return forceLoop && isOverflowing(emblaApi); // auto
    };

    const syncEmblaOptions = () => {
      if (!emblaApi?.reInit) return;

      const shouldLoopNow = computeLoopShould();
      const containScroll = (centerMode || shouldLoopNow) ? false : "trimSnaps";
      const nextAlign = align;

      const changed =
        lastLoop !== shouldLoopNow ||
        lastContainScroll !== containScroll ||
        lastAlign !== nextAlign;

      lastLoop = shouldLoopNow;
      lastContainScroll = containScroll;
      lastAlign = nextAlign;

      if (changed) {
        emblaApi.reInit({
          loop: shouldLoopNow,
          dragFree,
          skipSnaps,
          align: nextAlign,
          containScroll
        });

        if (!isOverflowing(emblaApi)) emblaApi.scrollTo(0, true);
      }

      // reset anchors
      lastSlideProgRaw = null;
      virtualSlideProg = getIndexProgressLoop();
      virtualProg = clamp(emblaApi.scrollProgress?.() ?? 0, 0, 1);

      updatePrevNextState();
      updateVirtualProgress();
      updateScrollbar("settle");
      logState("syncEmblaOptions");
    };

    const requestSync = rafThrottle(syncEmblaOptions);

    const resetVirtual = () => {
      lastSlideProgRaw = null;
      virtualSlideProg = getIndexProgressLoop();
      virtualProg = clamp(emblaApi.scrollProgress?.() ?? 0, 0, 1);
      lastSlideIndex = getSelectedSlideIndex();

      updateScrollbar("settle");
      logState("resetVirtual(init/reInit)");
    };

    emblaApi.on("init", () => { requestSync(); resetVirtual(); });
    emblaApi.on("reInit", () => { requestSync(); resetVirtual(); });

    emblaApi.on("select", () => {
      if (isLooping()) {
        const n = Math.max(1, getSlideCount());
        const curSlide = getSelectedSlideIndex();

        if (lastSlideIndex != null && n > 1) {
          // last -> 0
          if (lastSlideIndex === n - 1 && curSlide === 0) {
            startWrapSlide(1); // ✅从右滑到左
          }
          // 0 -> last
          else if (lastSlideIndex === 0 && curSlide === n - 1) {
            startWrapSlide(-1);
          }
        }

        lastSlideIndex = curSlide;
      }

      updatePrevNextState();
      logState("select");
    });

    emblaApi.on("scroll", () => {
      if (wrapAnimActive) return; // ✅wrap 动画期间，忽略 Embla 的 scroll 更新
      updateVirtualProgress();
      updateScrollbar("scroll");
    });

    emblaApi.on("settle", () => {
      if (wrapAnimActive) return; // ✅wrap 动画期间，不要让 settle 干扰
      if (isLooping()) {
        cancelSmooth();
        const p = getIndexProgressLoop();
        virtualSlideProg = p;
        lastSlideProgRaw = p;
        updateScrollbar("settle");
      } else {
        updateScrollbar("settle");
      }
      logState("settle");
    });

    /* ---------- Tween Scale（只读 .embla__scale，不自动创建） ---------- */
    let slides = [];
    let tweenNodes = [];
    let tweenFactor = 0;
    let tweenEnabledLast = false;

    const setTweenNodes = (api) => {
      slides = api.slideNodes();
      tweenNodes = slides.map((slide) => slide.querySelector(":scope > .embla__scale"));
    };

    const setTweenFactor = (api) => {
      tweenFactor = TWEEN_FACTOR_BASE * api.scrollSnapList().length;
    };

    const clearTween = () => {
      if (!tweenNodes.length) return;
      tweenNodes.forEach((n) => { if (n) n.style.transform = ""; });
      slides.forEach((s) => s.classList.remove("is-active"));
    };

    const setActive = (idx) => {
      if (!shouldTween()) return;
      slides.forEach((s, i) => s.classList.toggle("is-active", i === idx));
    };

    const tweenScale = (api, eventName) => {
      const enabled = shouldTween();
      if (!enabled) {
        if (tweenEnabledLast) clearTween();
        tweenEnabledLast = false;
        return;
      }
      tweenEnabledLast = true;

      if (!tweenNodes.length) setTweenNodes(api);

      const engine = api.internalEngine();
      const scrollProgress = api.scrollProgress();
      const slidesInView = api.slidesInView();
      const isScrollEvent = eventName === "scroll";

      api.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex] || [];

        slidesInSnap.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          const node = tweenNodes[slideIndex];
          if (!node) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();
              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);
                if (sign === -1) diffToTarget = scrollSnap - (1 + scrollProgress);
                if (sign === 1) diffToTarget = scrollSnap + (1 - scrollProgress);
              }
            });
          }

          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor);
          const t = clamp(tweenValue, 0, 1);
          const scale = SCALE_MIN + (SCALE_MAX - SCALE_MIN) * t;

          node.style.transform = `scale(${scale})`;
        });
      });
    };

    const lockViewportMinHeight = () => {
      if (!shouldTween()) {
        viewportNode.style.removeProperty("--fc-viewport-minh");
        return;
      }
      if (!tweenNodes.length) setTweenNodes(emblaApi);

      const nodes = tweenNodes.filter(Boolean);
      if (!nodes.length) return;

      const old = nodes.map((n) => n.style.transform);
      nodes.forEach((n) => (n.style.transform = ""));

      let maxH = 0;
      slides.forEach((s) => {
        const h = s.getBoundingClientRect().height;
        if (h > maxH) maxH = h;
      });

      const targetH = Math.ceil(maxH * Math.max(1, SCALE_MAX));
      if (targetH > 0) viewportNode.style.setProperty("--fc-viewport-minh", `${targetH}px`);

      nodes.forEach((n, i) => (n.style.transform = old[i] || ""));
    };

    const onInitLike = () => {
      setTweenNodes(emblaApi);
      setTweenFactor(emblaApi);

      updatePrevNextState();
      updateScrollbar("settle");

      if (shouldTween()) {
        setActive(emblaApi.selectedScrollSnap());
        tweenScale(emblaApi);
        requestAnimationFrame(lockViewportMinHeight);
      } else {
        clearTween();
        viewportNode.style.removeProperty("--fc-viewport-minh");
      }
    };

    onInitLike();

    emblaApi.on("select", () => {
      if (!shouldTween()) return;
      setActive(emblaApi.selectedScrollSnap());
      tweenScale(emblaApi);
    });
    emblaApi.on("scroll", (api, evt) => tweenScale(api, evt));
    emblaApi.on("settle", (api, evt) => {
      tweenScale(api, evt);
      if (shouldTween()) requestAnimationFrame(lockViewportMinHeight);
    });
    emblaApi.on("reInit", () => {
      setTweenNodes(emblaApi);
      setTweenFactor(emblaApi);
      if (shouldTween()) {
        setActive(emblaApi.selectedScrollSnap());
        tweenScale(emblaApi);
        lockViewportMinHeight();
      } else {
        clearTween();
        viewportNode.style.removeProperty("--fc-viewport-minh");
      }
    });

    /* ---------- ResizeObserver ---------- */
    let ro = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(() => requestSync());
      ro.observe(viewportNode);
      const container = emblaApi.containerNode?.();
      if (container) ro.observe(container);
      if (scrollbar) ro.observe(scrollbar);
    } else {
      let winTimer = null;
      window.addEventListener("resize", () => {
        clearTimeout(winTimer);
        winTimer = setTimeout(() => requestSync(), 150);
      });
    }

    /* ---------- AutoScroll hover control (benefits) ---------- */
    const autoScroll = emblaApi.plugins?.()?.autoScroll;
    if (autoscrollEnabled && autoScroll) {
      try { autoScroll.play(); } catch (_) { }

      let resumeTimer = null;
      const bindHover = () => {
        viewportNode.querySelectorAll(".embla__slide").forEach((slide) => {
          slide.addEventListener("mouseenter", () => {
            if (!autoScrollStopOnMouseEnter) return;
            clearTimeout(resumeTimer);
            try { autoScroll.stop(); } catch (_) { }
          }, { passive: true });

          slide.addEventListener("mouseleave", () => {
            if (!autoScrollStopOnMouseEnter) return;
            clearTimeout(resumeTimer);
            resumeTimer = setTimeout(() => {
              try { autoScroll.play(); } catch (_) { }
            }, autoScrollResumeDelay);
          }, { passive: true });
        });
      };

      bindHover();
      emblaApi.on("reInit", () => {
        try { autoScroll.play(); } catch (_) { }
        bindHover();
      });
    }

    /* ---------- Destroy hook ---------- */
    root.__emblaDestroy = () => {
      try { ro?.disconnect?.(); } catch (_) { }
      cancelSmooth();
      cancelWrapAnim();
      try { emblaApi?.destroy?.(); } catch (_) { }
    };

    // Initial sync
    requestSync();
  }

  document.addEventListener("DOMContentLoaded", () => {
    const roots = new Set();
    document.querySelectorAll('[data-embla-init="true"]').forEach((el) => roots.add(el));
    document.querySelectorAll('[data-embla^="benefits"]').forEach((el) => roots.add(el));
    roots.forEach(initEmbla);
  });

  window.__initEmblaCarousel = initEmbla;
})();